import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

// Screen layers, bottom to top. Page chrome stays below LAYER.overlay; sheets
// and modals use LAYER.modal; a confirmation opened from a modal must sit
// above it, so it uses LAYER.confirm.
export const LAYER = { overlay: 50, modal: 100, confirm: 200 };

const ConfirmSheet = ({ message, confirmLabel, cancelLabel, danger, onDone }) => {
  const confirmRef = useRef(null);
  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = event => { if (event.key === 'Escape') onDone(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDone]);
  return (
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: LAYER.confirm, background: 'rgba(0,0,0,0.7)' }}
      onClick={() => onDone(false)} data-testid="confirm-dialog">
      <div role="alertdialog" aria-modal="true" aria-labelledby="confirm-dialog-message"
        className="w-full max-w-sm rounded-xl p-5 space-y-4" onClick={event => event.stopPropagation()}
        style={{ background: 'var(--bg-card, #141414)', border: '1px solid var(--border-light, #262626)' }}>
        <p id="confirm-dialog-message" className="text-sm whitespace-pre-line" style={{ color: 'var(--text-primary, #E5E5E5)' }}>{message}</p>
        <div className="flex gap-2">
          <button type="button" onClick={() => onDone(false)} data-testid="confirm-dialog-cancel"
            className="flex-1 py-2.5 rounded-lg text-sm font-medium" style={{ background: 'var(--bg-muted, #262626)', color: 'var(--text-primary, #E5E5E5)' }}>
            {cancelLabel}
          </button>
          <button type="button" ref={confirmRef} onClick={() => onDone(true)} data-testid="confirm-dialog-ok"
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold text-white ${danger ? 'bg-rose-600 hover:bg-rose-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// Promise-based replacement for window.confirm that works on touch screens,
// matches the app theme and always renders above open modals.
export const confirmDialog = (message, { confirmLabel = 'Тийм', cancelLabel = 'Болих', danger = false } = {}) =>
  new Promise(resolve => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    let settled = false;
    const done = answer => {
      if (settled) return;
      settled = true;
      root.unmount();
      host.remove();
      resolve(answer);
    };
    root.render(<ConfirmSheet message={message} confirmLabel={confirmLabel} cancelLabel={cancelLabel}
      danger={danger} onDone={done} />);
  });

export default confirmDialog;
